import {
  euCountries,
  euroCountries,
  shippingCountries,
} from './assets/js/constants/store-config'
import ShippingMethod from '~/models/ShippingMethod'

function getCountryNameFromCode(iso) {
  return shippingCountries.find((country) => country[0] === iso)?.[1]
}

function isShippingDomestic(state) {
  return (state.shippingCountry || 'GB').toLowerCase() === 'gb'
}

function isUK(state) {
  return (state.shippingCountry || 'GB').toUpperCase() === 'GB'
}

function isEU(state) {
  return (
    !(state.shippingCountry || 'GB').toUpperCase() === 'GB' &&
    state.euCountries.includes((state.shippingCountry || 'GB').toUpperCase())
  )
}

export const useStorefrontStore = defineStore('storefrontStore', {
  state: () => ({
    restored: false,
    shippingCountry: useRoute()?.params?.locale?.toString().split('-')[1].toUpperCase() || 'GB',
    sg18pid: null,
    newSession: false,
    shippingList: {},
    ipAddress: null,
    shippingNotificationMessage: 'All duties and taxes included',
    ddpDestinations: [
      'AT',
      'BE',
      'CZ',
      'DK',
      'FR',
      'DE',
      'HU',
      'IT',
      'LV',
      'LT',
      'LU',
      'NL',
      'PL',
      'PT',
      'SK',
      'SI',
      'SE',
      'US',
      'AU',
      'NZ',
    ],
  }),

  actions: {
    RESET() {
      this.$reset()
    },
    SET_SG18PID(sg18pid) {
      this.sg18pid = (sg18pid || this.sg18pid).split(',').pop()
    },
    CREATE_SG18PID() {
      const chars =
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
      const sessionId = Array(20)
        .join()
        .split(',')
        .map(() => chars.charAt(Math.floor(Math.random() * chars.length)))
        .join('')

      this.sg18pid = sessionId
      this.newSession = true

      return sessionId
    },
    DISMISS_NEW_SESSION() {
      this.newSession = false
    },
    SET_SHIPPING([methods, country]) {
      this.shippingList = { ...this.shippingList, [country]: methods }
    },
    CHANGE_SHIPPING_COUNTRY(country) {
      this.shippingCountry = country || this.shippingCountry || 'GB'
    },
    SET_IP_ADDRESS(ipAddress) {
      this.ipAddress = ipAddress
    },
    SET_STATE_RESTORED() {
      this.restored = true
    },
    async GET_SHIPPING() {
      const { $sentry, $i18n } = useNuxtApp()

      // Only fetch if this country hasn't been fetched before
      if (this.shippingList[this.shippingCountry]) return

      try {
        const shippingCountry = this.shippingCountry || 'GB'
        // The below route will automatically be redirected to the current locale
        const { data: response } = await useFetch(
          `/${$i18n.locale.value}/shipping/${shippingCountry}/methods`
        )

        this.SET_SHIPPING([response.value, shippingCountry])
      } catch (error) {
        $sentry.captureException(error)
        console.error(error)
      }
    }
  },

  getters: {
    getSg18pid: (state) => {
      if (state.sg18pid) return state.sg18pid.split(',').pop()
      return null
    },
    shippingCountries: () => shippingCountries,
    validCountryCode: (state) => (iso) => iso in state.shippingList,
    countryNameFromCode: () => {
      return (iso) => getCountryNameFromCode(iso)
    },
    getShippingCountry: (state) => (state.shippingCountry || 'GB').toUpperCase(),
    shippingCountryName: (state) =>
      getCountryNameFromCode((state.shippingCountry || 'GB').toUpperCase()),
    isShippingDomestic: (state) => isShippingDomestic(state),
    shippingDetails: (state) => {
      const userStore = useUserStore()

      const details = {
        isDomestic: isShippingDomestic(state),
        methods: (
          state.shippingList[(state.shippingCountry || 'GB').toUpperCase()] ||
          []
        ).map((method) => new ShippingMethod(method)),
        country: getCountryNameFromCode(
          (state.shippingCountry || 'GB').toUpperCase()
        ),
      }

      // Get an array of the enabled methods sorted by price in ascending order
      if (!userStore.uuid) userStore.setUUID()
      details.enabledMethods = details.methods
        .filter((method) =>
          method.testIsEnabled(
            userStore.uuid,
            details.methods,
            state.shippingCountry
          )
        )
        .sort((a, b) => a.price - b.price)

      // Get the preferred method as the cheapest that is not a slow method and is not click and collect
      details.preferredMethod =
        details.enabledMethods.find(
          (method) => !method.isSlowMethod && !method.isClickAndCollect
        ) || details.enabledMethods[0]

      // Get the cheapest method
      details.cheapestMethod = details.enabledMethods[0]

      return details
    },
    euroCountries: () => euroCountries,
    euCountries: () => euCountries,
    isUK: (state) => isUK(state),
    isEU: (state) => isEU(state),
    isROW: (state) => !isEU(state) && !isUK(state),
    isDDP: (state) =>
      state.ddpDestinations.includes(
        ((state.shippingCountry || 'GB').toUpperCase() || '').toUpperCase()
      ),
    preferredShippingMethodWithThreshold: (state) =>
      state.shippingDetails.preferredMethod?.freeOverAmountCents
        ? state.shippingDetails.preferredMethod
        : state.shippingDetails.enabledMethods.filter(
            (method) => method.freeOverAmountCents
          )[0],
  },

  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
    pick: ['shippingCountry'],
  }
})
